// File upload security utility

export interface FileValidationOptions {
  maxSize?: number; // in MB
  allowedTypes?: string[];
  maxCount?: number;
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  validFiles: File[];
}

/**
 * Safe image file extensions
 */
export const SAFE_IMAGE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'
];

/**
 * Allowed image MIME types
 */
export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp'
];

/**
 * Dangerous file extensions (never allowed)
 */
const DANGEROUS_EXTENSIONS = [
  'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar',
  'zip', 'rar', '7z', 'tar', 'gz', 'php', 'asp', 'jsp', 'sh'
];

/**
 * Extract file extension
 */
function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

/**
 * Convert file size to MB
 */
function bytesToMB(bytes: number): number {
  return bytes / (1024 * 1024);
}

/**
 * Validate if file is actually an image (checks both MIME type and extension)
 */
function validateImageFile(file: File): boolean {
  const extension = getFileExtension(file.name);
  const mimeType = file.type;
  
  return SAFE_IMAGE_EXTENSIONS.includes(extension) && 
         ALLOWED_IMAGE_TYPES.includes(mimeType);
}

/**
 * Check if file is dangerous
 */
function isDangerousFile(file: File): boolean {
  const extension = getFileExtension(file.name);
  return DANGEROUS_EXTENSIONS.includes(extension);
}

/**
 * Validate file array
 */
export function validateFiles(
  files: File[], 
  options: FileValidationOptions = {}
): FileValidationResult {
  const {
    maxSize = 5, // default 5MB
    allowedTypes = ALLOWED_IMAGE_TYPES,
    maxCount = 3 // default max 3
  } = options;

  const errors: string[] = [];
  const validFiles: File[] = [];

  // Check file count
  if (files.length > maxCount) {
    errors.push(`You can upload a maximum of ${maxCount} files.`);
    return { isValid: false, errors, validFiles };
  }

  for (const file of files) {
    let fileErrors: string[] = [];

    // 1. File name length limit
    if (file.name.length > 100) {
      fileErrors.push(`File name is too long: ${file.name.substring(0, 30)}...`);
    }

    // 2. Dangerous file check
    if (isDangerousFile(file)) {
      fileErrors.push(`Security risk detected: ${file.name}`);
    }

    // 3. File size check
    const fileSizeMB = bytesToMB(file.size);
    if (fileSizeMB > maxSize) {
      fileErrors.push(`File size exceeds ${maxSize}MB: ${file.name} (${fileSizeMB.toFixed(1)}MB)`);
    }

    // 4. MIME type check (when only images are allowed)
    if (allowedTypes === ALLOWED_IMAGE_TYPES && !validateImageFile(file)) {
      fileErrors.push(`Only image files are allowed: ${file.name}`);
    }

    // 5. Empty file check
    if (file.size === 0) {
      fileErrors.push(`Empty files cannot be uploaded: ${file.name}`);
    }

    // 6. Special character check in filename (XSS prevention)
    if (/[<>:"/\\|?*]/.test(file.name)) {
      fileErrors.push(`File name contains special characters: ${file.name}`);
    }

    if (fileErrors.length === 0) {
      validFiles.push(file);
    } else {
      errors.push(...fileErrors);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    validFiles
  };
}

/**
 * Image file specific validation
 */
export function validateImageFiles(files: File[], maxCount: number = 3): FileValidationResult {
  return validateFiles(files, {
    maxSize: 5, // 5MB limit
    allowedTypes: ALLOWED_IMAGE_TYPES,
    maxCount
  });
}

/**
 * Pre-validation before file upload (client-side)
 */
export function preValidateFileUpload(
  inputElement: HTMLInputElement,
  options?: FileValidationOptions
): FileValidationResult {
  const files = inputElement.files;
  if (!files) {
    return { isValid: true, errors: [], validFiles: [] };
  }

  return validateFiles(Array.from(files), options);
}